import { useState, useRef, useEffect, type ReactNode } from 'react';
import { Moon, Sun, LogOut, ChevronUp, Globe, Map } from 'lucide-react';
import { GsBuildStamp } from './GsBuildStamp';
import { GsContextSwitcher } from './GsContextSwitcher';
import type { GsContextSwitcherProps } from './GsContextSwitcher';
// Sous-chemin LEGER (React seul — pas i18next) : les apps non migrees i18n
// n'embarquent rien de plus ; sans GsLocaleProvider, useLocale() = null et
// l'item Langue est masque (fini le placeholder mort « FR » — O2 BLITZ-I18N).
import { useLocale, SUPPORTED_LOCALES } from '@gs/i18n/localeContext';

export interface GsUserMenuUser {
  name: string;
  email?: string;
  role: string;
  initials: string;
  photoUrl?: string;
}

/** Libellés i18n des items du menu (défauts FR — apps migrées : t('common:chrome.*')). */
export interface GsUserMenuLabels {
  lightMode?: string;
  darkMode?: string;
  language?: string;
  reviewTour?: string;
  logout?: string;
}

export interface GsUserMenuProps {
  user: GsUserMenuUser;
  isDark?: boolean;
  onToggleDark?: () => void;
  onLogout?: () => void;
  /** Libellés traduits (pas de t() dans @gs/ui — props, défauts FR). */
  labels?: GsUserMenuLabels;
  /** Tour guidé / onboarding — affiche « Revoir le tour » si défini */
  onStartTour?: () => void;
  /** Clic sur l’avatar — ex. ouvrir un panneau profil (séparé du menu déroulant) */
  onAvatarClick?: () => void;
  variant?: 'navy' | 'light';
  appName?: string;
  /**
   * @deprecated Ignoree : la version affichee est celle de la construction, lue de
   * `/api/version` (GsBuildStamp). Les apps passaient ici un « 0.1.0 » en dur.
   */
  version?: string;
  /** Quand la sidebar est rétractée — affiche uniquement l'avatar initiales */
  collapsed?: boolean;
  /**
   * Sélecteur de contexte corporate (A3) — intégré sous le rôle dans le bloc
   * utilisateur (CONTEXT-IN-AVATAR). Signature inchangée côté GsSidebar.
   */
  contextSwitcher?: GsContextSwitcherProps;
  /**
   * Contenu ajouté dans le pied du menu, à la suite de la version (ex. le lien « Quoi de neuf »
   * de GsSidebar). Reçoit `fermer`, qui referme le menu. Absent = pied inchangé.
   */
  footerExtra?: (fermer: () => void) => ReactNode;
}

export function GsUserMenu({
  user, isDark = false, onToggleDark, onLogout, onStartTour, onAvatarClick,
  variant = 'light', appName, collapsed = false, labels, contextSwitcher, footerExtra,
}: GsUserMenuProps) {
  const [open, setOpen] = useState(false);
  const localeCtx = useLocale();
  const lightModeLabel = labels?.lightMode ?? 'Mode clair';
  const darkModeLabel = labels?.darkMode ?? 'Mode sombre';
  const languageLabel = labels?.language ?? (localeCtx?.locale === 'en' ? 'Language' : 'Langue');
  const reviewTourLabel = labels?.reviewTour ?? 'Revoir le tour';
  const logoutLabel = labels?.logout ?? 'Se déconnecter';
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = `gs-user-menu-${user.initials}`;
  const isLight = variant === 'light';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const footBorder = isLight
    ? 'border-t border-slate-200 dark:border-slate-800'
    : 'border-t-[0.5px] border-t-[rgba(255,255,255,0.06)]';
  const footName = isLight ? 'text-slate-900 dark:text-slate-100' : 'text-white';
  const footRole = isLight ? 'text-slate-500 dark:text-slate-400' : 'text-slate-700';
  const menuBg = isLight
    ? 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700'
    : 'bg-slate-800 border border-white/10';
  const menuText = isLight ? 'text-slate-700 dark:text-slate-200' : 'text-slate-200';
  const menuHover = isLight ? 'hover:bg-slate-100 dark:hover:bg-slate-800' : 'hover:bg-white/10';
  const menuDivider = isLight
    ? 'border-t border-slate-100 dark:border-slate-800'
    : 'border-t border-white/10';
  const menuMuted = isLight ? 'text-slate-500 dark:text-slate-400' : 'text-slate-400';

  const initialsCircle = (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-500 text-chip font-semibold text-white">
      {user.initials}
    </div>
  );

  const embeddedContextSwitcher = contextSwitcher ? (
    <GsContextSwitcher
      {...contextSwitcher}
      embedded
      collapsed={collapsed}
      variant={variant}
    />
  ) : null;

  const userTextBlock = (
    <div className="min-w-0 flex-1 text-left">
      <div className={`truncate text-meta font-medium ${footName}`}>{user.name}</div>
      <div className={`truncate text-chip ${footRole}`}>{user.role}</div>
      {!collapsed ? embeddedContextSwitcher : null}
    </div>
  );

  const menuTriggerButton = (
    <button
      ref={triggerRef}
      type="button"
      onClick={() => setOpen((p) => !p)}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
      aria-label={`Menu utilisateur — ${user.name}`}
      title={collapsed && !onAvatarClick ? user.name : undefined}
      className={[
        'flex items-center transition-colors hover:bg-black/5 dark:hover:bg-white/5 touch-manipulation',
        collapsed && onAvatarClick
          ? 'w-full justify-center px-0 pb-2 pt-0'
          : 'w-full gap-2.5 px-3 py-3',
        !collapsed && onAvatarClick ? 'min-w-0 flex-1 gap-2.5 px-2 py-3' : '',
        !collapsed && !onAvatarClick ? 'w-full' : '',
      ].join(' ')}
    >
      {!collapsed && (
        <>
          {!onAvatarClick ? initialsCircle : null}
          {userTextBlock}
          <ChevronUp size={13} className={`shrink-0 transition-transform ${footRole} ${open ? 'rotate-0' : 'rotate-180'}`} aria-hidden />
        </>
      )}
      {collapsed && onAvatarClick ? (
        <ChevronUp size={13} className={`shrink-0 transition-transform ${footRole} ${open ? 'rotate-0' : 'rotate-180'}`} aria-hidden />
      ) : null}
    </button>
  );

  return (
    <div ref={containerRef} className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}>
      {onAvatarClick ? (
        collapsed ? (
          <div className={`flex flex-col items-stretch ${footBorder}`}>
            <button
              type="button"
              onClick={() => onAvatarClick()}
              className="flex w-full items-center justify-center px-0 py-2.5 transition-colors hover:bg-black/5 dark:hover:bg-white/5 touch-manipulation"
              aria-label={`Ouvrir le profil — ${user.name}`}
            >
              {initialsCircle}
            </button>
            {embeddedContextSwitcher}
            {menuTriggerButton}
          </div>
        ) : (
          <div className={`flex w-full items-stretch ${footBorder}`}>
            <button
              type="button"
              onClick={() => onAvatarClick()}
              className="flex shrink-0 items-center px-3 py-3 transition-colors hover:bg-black/5 dark:hover:bg-white/5 touch-manipulation"
              aria-label="Ouvrir le profil"
            >
              {initialsCircle}
            </button>
            {menuTriggerButton}
          </div>
        )
      ) : (
        collapsed ? (
          <div className={`flex flex-col items-stretch ${footBorder}`}>
            <button
              ref={triggerRef}
              type="button"
              onClick={() => setOpen((p) => !p)}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-controls={open ? menuId : undefined}
              aria-label={`Menu utilisateur — ${user.name}`}
              title={user.name}
              className="flex w-full items-center justify-center px-0 py-3 transition-colors hover:bg-black/5 dark:hover:bg-white/5 touch-manipulation"
            >
              {initialsCircle}
            </button>
            {embeddedContextSwitcher}
          </div>
        ) : (
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen((p) => !p)}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
            aria-label={`Menu utilisateur — ${user.name}`}
            title={collapsed ? user.name : undefined}
            className={[
              'flex w-full items-center transition-colors hover:bg-black/5 dark:hover:bg-white/5 touch-manipulation',
              footBorder,
              'gap-2.5 px-3 py-3',
            ].join(' ')}
          >
            {initialsCircle}
            {userTextBlock}
            <ChevronUp size={13} className={`shrink-0 transition-transform ${footRole} ${open ? 'rotate-0' : 'rotate-180'}`} aria-hidden />
          </button>
        )
      )}

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Options utilisateur"
          className={[
            'absolute z-50 overflow-hidden rounded-xl shadow-xl',
            collapsed
              ? 'left-full bottom-0 right-auto ml-2 mb-0 w-52'
              : 'bottom-full left-0 right-0 mb-1',
            menuBg,
          ].join(' ')}
        >
          <div className="px-3 py-2.5">
            <div className={`text-meta font-semibold ${footName}`}>{user.name}</div>
            <div className={`mt-0.5 text-chip ${footRole}`}>{user.role}</div>
            {user.email && <div className={`mt-1 text-chip ${menuMuted}`}>{user.email}</div>}
          </div>

          <div className={menuDivider} />

          {onToggleDark && (
            <button type="button" role="menuitem" onClick={onToggleDark}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-meta ${menuText} ${menuHover} transition-colors touch-manipulation`}>
              <span className="flex items-center gap-2">
                {isDark ? <Sun size={14} className="text-amber-400" /> : <Moon size={14} />}
                {isDark ? lightModeLabel : darkModeLabel}
              </span>
              <span className={`relative h-4 w-7 rounded-full transition-colors ${isDark ? 'bg-brand-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${isDark ? 'translate-x-[14px]' : 'translate-x-0.5'}`} />
              </span>
            </button>
          )}

          {localeCtx && (
            <>
              <div className={menuDivider} />
              <div role="menuitem" tabIndex={-1}
                className={`flex w-full items-center justify-between px-3 py-2 text-meta ${menuText}`}>
                <span className="flex items-center gap-2">
                  <Globe size={14} />
                  {languageLabel}
                </span>
                <span className={`flex items-center gap-0.5 rounded-md p-0.5 ${isLight ? 'bg-slate-100 dark:bg-slate-800' : 'bg-white/10'}`}>
                  {SUPPORTED_LOCALES.map((loc) => (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => localeCtx.setLocale(loc)}
                      aria-pressed={localeCtx.locale === loc}
                      className={[
                        'rounded-sm px-1.5 py-0.5 text-chip font-semibold transition-colors touch-manipulation',
                        localeCtx.locale === loc
                          ? isLight
                            ? 'bg-white text-slate-800 shadow-xs dark:bg-slate-600 dark:text-slate-100'
                            : 'bg-white/90 text-slate-900 shadow-xs'
                          : `${menuMuted} hover:text-slate-700 dark:hover:text-slate-200`,
                      ].join(' ')}
                    >
                      {loc.toUpperCase()}
                    </button>
                  ))}
                </span>
              </div>
            </>
          )}

          {onStartTour && (
            <>
              <div className={menuDivider} />
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onStartTour();
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-meta text-slate-500 transition-colors hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800 touch-manipulation"
              >
                <Map size={14} className="shrink-0" />
                {reviewTourLabel}
              </button>
            </>
          )}

          {onLogout && (
            <>
              <div className={menuDivider} />
              <button type="button" role="menuitem"
                onClick={() => { setOpen(false); onLogout(); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-meta text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30 transition-colors touch-manipulation">
                <LogOut size={14} />
                {logoutLabel}
              </button>
            </>
          )}

          {/* Pied du menu : nom de l'app et VERSION CONSTRUITE (GsBuildStamp, lue de /api/version).
              La prop `version` est ignoree : les apps y posaient un « 0.1.0 » en dur. */}
          <div className={menuDivider} />
          <div className={`px-3 py-2 text-chip ${menuMuted}`}>
            {appName}
            <GsBuildStamp lead={appName ? ' · ' : undefined} />
            {footerExtra?.(() => setOpen(false))}
          </div>
        </div>
      )}
    </div>
  );
}
